<template>
  <section class="dbud" :class="{ 'dbud--flush': flush }">
    <div v-if="!flush" class="dbud__head">
      <div class="dbud__label">Demanda por presupuesto · últimos {{ range }} días</div>
    </div>

    <div class="dbud__controls">
      <div class="seg seg--sm">
        <button type="button" class="seg__btn" :class="{ on: operation === 'sell' }" @click="setOp('sell')">Compra</button>
        <button type="button" class="seg__btn" :class="{ on: operation === 'rent' }" @click="setOp('rent')">Alquiler</button>
      </div>
      <div class="dbud__chips">
        <button
          v-for="t in types" :key="t.label"
          type="button" class="chip" :class="{ on: propertyType === t.value }"
          @click="setType(t.value)"
        >{{ t.label }}</button>
      </div>
      <div class="seg seg--sm dbud__cur">
        <button type="button" class="seg__btn" :class="{ on: currency === 'USD' }" @click="setCur('USD')">USD</button>
        <button type="button" class="seg__btn" :class="{ on: currency === 'ARS' }" @click="setCur('ARS')">ARS</button>
      </div>
    </div>

    <div class="dbud__grid">
      <!-- preset bands -->
      <div class="dbud__bands">
        <div v-if="loading" class="dbud__empty">Cargando…</div>
        <template v-else-if="totalViews">
          <div v-for="(b, i) in bands" :key="i" class="dbud__row dbud__row--click" @click="openSegment(b)">
            <span class="dbud__bandlbl">{{ currency }} {{ b.label }}</span>
            <span class="dbud__track"><span class="dbud__fill" :style="{ width: barPct(b) }"></span></span>
            <span class="dbud__val">{{ b.views }}<em>{{ pct(b.views) }}%</em></span>
            <span class="dbud__go">›</span>
          </div>
        </template>
        <div v-else class="dbud__empty">Sin demanda registrada para este filtro en el período.</div>
      </div>

      <!-- custom range -->
      <div class="dbud__customcard">
        <div class="dbud__sublabel">Rango personalizado <span class="dbud__hint">{{ currency }}</span></div>
        <div class="dbud__rangeinputs">
          <input v-model.number="customMin" type="number" min="0" placeholder="Mín" @input="debouncedFetch" />
          <span class="dbud__dash">—</span>
          <input v-model.number="customMax" type="number" min="0" placeholder="Máx" @input="debouncedFetch" />
        </div>
        <div v-if="custom && (customMin || customMax)" class="dbud__customresult dbud__customresult--click" @click="openSegment(custom)">
          <div class="dbud__cnum">{{ custom.views }} <span class="dbud__cpct">{{ pct(custom.views) }}%</span></div>
          <div class="dbud__clbl">vistas en {{ currency }} {{ custom.label }}</div>
          <div class="dbud__csub">{{ custom.searches }} búsqueda{{ custom.searches === 1 ? '' : 's' }} con ese filtro · ver detalle ›</div>
        </div>
        <div v-else class="dbud__customhint">Ingresá un rango para ver cuánta demanda concentra.</div>
      </div>
    </div>

    <div class="dbud__foot">
      Demanda = vistas reales de propiedades en el período · {{ totalViews }} vistas, {{ totalSearches }} búsquedas con filtro de precio. Tocá una banda para ver el detalle.
    </div>

    <budget-segment-drawer v-model="drawerOpen" :params="drawerParams" :title="drawerTitle" />
  </section>
</template>

<script>
import { DemandByBudget } from "@/graphql/queries/analytics";
import { localDateKey, BROWSER_TZ } from "@/utils/charts";
import BudgetSegmentDrawer from "@/components/reports/BudgetSegmentDrawer";

export default {
  components: { BudgetSegmentDrawer },

  props: {
    range: { type: Number, default: 30 },
    // rendered inside a ReportPanel, which supplies the card and the title
    flush: { type: Boolean, default: false }
  },

  data: () => ({
    report: null,
    loading: false,
    operation: "sell",
    propertyType: null,
    currency: "USD",
    customMin: null,
    customMax: null,
    drawerOpen: false,
    drawerParams: {},
    drawerTitle: "",
    types: [
      { label: "Todos", value: null },
      { label: "Casa", value: "house" },
      { label: "Depto", value: "apartment" },
      { label: "Terreno", value: "terrain" },
      { label: "Local", value: "salon" }
    ],
    _debounce: null
  }),

  created() {
    this.fetch();
  },

  watch: {
    range() {
      this.fetch();
    }
  },

  computed: {
    rangeDates() {
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - (this.range - 1));
      return { from: localDateKey(from), to: localDateKey(to) };
    },
    bands() {
      return (this.report && this.report.bands) || [];
    },
    custom() {
      return this.report && this.report.custom;
    },
    totalViews() {
      return (this.report && this.report.totalViews) || 0;
    },
    totalSearches() {
      return (this.report && this.report.totalSearches) || 0;
    },
    maxViews() {
      return Math.max(1, ...this.bands.map(b => b.views));
    }
  },

  methods: {
    setOp(v) {
      this.operation = v;
      this.currency = v === "sell" ? "USD" : "ARS"; // sales quoted USD, rentals ARS
      this.fetch();
    },
    setType(v) {
      this.propertyType = v;
      this.fetch();
    },
    setCur(v) {
      this.currency = v;
      this.fetch();
    },
    pct(n) {
      return this.totalViews ? Math.round((n / this.totalViews) * 100) : 0;
    },
    barPct(b) {
      return Math.round((b.views / this.maxViews) * 100) + "%";
    },
    debouncedFetch() {
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => this.fetch(), 400);
    },
    openSegment(band) {
      const opL = this.operation === "sell" ? "Compra" : "Alquiler";
      const tL = (this.types.find(t => t.value === this.propertyType) || {}).label || "Todos";
      this.drawerParams = {
        operationType: this.operation,
        propertyType: this.propertyType,
        currency: this.currency,
        range: this.range,
        min: band.min != null ? band.min : null,
        max: band.max != null ? band.max : null
      };
      this.drawerTitle = `${opL} · ${tL} · ${this.currency} ${band.label}`;
      this.drawerOpen = true;
    },
    fetch() {
      this.loading = true;
      this.$apollo
        .query({
          query: DemandByBudget,
          fetchPolicy: "no-cache",
          variables: {
            operationType: this.operation,
            propertyType: this.propertyType,
            currency: this.currency,
            from: this.rangeDates.from,
            to: this.rangeDates.to,
            timeZone: BROWSER_TZ,
            customMin: Number.isFinite(this.customMin) ? this.customMin : null,
            customMax: Number.isFinite(this.customMax) ? this.customMax : null
          }
        })
        .then(res => {
          this.report = res.data.demandByBudget;
        })
        .catch(() => {
          this.report = null;
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style scoped>
.dbud { margin-top: 24px; }
.dbud--flush { margin-top: 0; padding: 16px 18px 18px; }
.dbud__head { margin-bottom: 12px; }
.dbud__label { font-size: 13px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #756f64; }

.dbud__controls { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-bottom: 16px; }
.seg { display: inline-flex; background: #f1ece3; border-radius: 9px; padding: 3px; gap: 2px; }
.seg__btn { border: none; background: transparent; cursor: pointer; font: inherit; font-size: 12.5px; font-weight: 600; color: #6a645a; border-radius: 7px; padding: 6px 13px; transition: background 0.15s, color 0.15s; }
.seg__btn.on { background: #fff; color: #1a1814; font-weight: 700; box-shadow: 0 1px 4px -1px rgba(40, 33, 22, 0.3); }
.dbud__cur { margin-left: auto; }

.dbud__chips { display: flex; flex-wrap: wrap; gap: 6px; }
.chip { border-radius: 999px; border: 1px solid #e3ddd2; background: #fff; cursor: pointer; font-weight: 600; font-size: 12px; color: #56504a; padding: 6px 12px; transition: all 0.15s; }
.chip.on { background: #1a1814; color: #fff; border-color: #1a1814; }

.dbud__grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 18px; }
@media (max-width: 900px) { .dbud__grid { grid-template-columns: 1fr; } }

.dbud__bands { background: #fff; border: 1px solid #ece7de; border-radius: 14px; padding: 16px 18px; display: flex; flex-direction: column; gap: 11px; }
.dbud__row { display: grid; grid-template-columns: 92px 1fr 72px 12px; align-items: center; gap: 12px; }
.dbud__row--click { cursor: pointer; border-radius: 7px; padding: 3px 6px; margin: 0 -6px; transition: background 0.12s; }
.dbud__row--click:hover { background: #faf6f0; }
.dbud__go { color: #c8bfb0; font-weight: 700; font-size: 16px; text-align: center; }
.dbud__row--click:hover .dbud__go { color: #b52118; }
.dbud__bandlbl { font-size: 12px; font-weight: 600; color: #1a1814; white-space: nowrap; }
.dbud__track { height: 8px; background: #f1ece3; border-radius: 4px; overflow: hidden; }
.dbud__fill { display: block; height: 100%; background: #b52118; border-radius: 4px; }
.dbud__val { font-size: 13px; font-weight: 700; color: #1a1814; text-align: right; white-space: nowrap; }
.dbud__val em { font-style: normal; color: #a39a8c; font-weight: 600; font-size: 11.5px; margin-left: 6px; }

.dbud__customcard { background: #fbf8f3; border: 1px solid #ece7de; border-radius: 14px; padding: 16px 18px; }
.dbud__sublabel { font-size: 10px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #a39a8c; margin-bottom: 12px; }
.dbud__hint { color: #c2b9aa; }
.dbud__rangeinputs { display: flex; align-items: center; gap: 8px; }
.dbud__rangeinputs input { width: 100%; border: 1px solid #e3ddd2; border-radius: 8px; padding: 9px 11px; font: inherit; font-size: 13px; color: #1a1814; background: #fff; }
.dbud__rangeinputs input:focus { outline: none; border-color: #b52118; }
.dbud__dash { color: #a39a8c; }
.dbud__customresult { margin-top: 14px; }
.dbud__customresult--click { cursor: pointer; }
.dbud__customresult--click:hover .dbud__cnum { color: #b52118; }
.dbud__cnum { font-size: 26px; font-weight: 800; color: #1a1814; line-height: 1; }
.dbud__cpct { font-size: 14px; font-weight: 700; color: #b52118; margin-left: 6px; }
.dbud__clbl { font-size: 12.5px; color: #756f64; margin-top: 5px; }
.dbud__csub { font-size: 11.5px; color: #a39a8c; margin-top: 3px; }
.dbud__customhint { margin-top: 14px; font-size: 12.5px; color: #a39a8c; line-height: 1.5; }

.dbud__empty { color: #a39a8c; font-size: 12.5px; padding: 8px 0; }
.dbud__foot { margin-top: 12px; font-size: 11.5px; color: #a39a8c; }
</style>
